import { MatterDocsFilters } from '@../../../backend/src/types/types';
import { useMutation, useQuery } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { FetchCaseDocsRes, FetchCaseDocsCountRes } from '@/api/types/queries';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export const useGetDocs = ({
  caseId,
  page,
  pageSize,
  filters,
  sortBy,
  sortOrder,
  isSubsetCreator = false,
}: {
  caseId: string | null;
  page: number;
  pageSize: number;
  filters: MatterDocsFilters;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  isSubsetCreator?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsRes>(
    [
      QueryEntity.CaseDocs,
      { caseId },
      {
        page,
        pageSize,
        sortBy,
        sortOrder,
        filters,
        isSubsetCreator,
      },
    ],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          page,
          pageSize,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
          filters: filters || undefined,
          isSubsetCreator: isSubsetCreator || undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/docs/search`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId },
  );
};

export const useGetDocsCount = ({
  caseId,
  pageSize,
  filters,
  isSubsetCreator = false,
}: {
  caseId: string | null;
  pageSize: number;
  filters: MatterDocsFilters;
  isSubsetCreator?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useQuery<FetchCaseDocsCountRes>(
    [
      QueryEntity.CaseDocsCount,
      { caseId },
      {
        pageSize,
        filters,
        isSubsetCreator,
      },
    ],
    async () => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          pageSize,
          filters,
          isSubsetCreator: isSubsetCreator || undefined,
        },
      });
      const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/docs/count`, fetchConfig);
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      return res.json();
    },
    { refetchOnWindowFocus: false, enabled: !!caseId, staleTime: Infinity },
  );
};

export const useGetDocIds = ({
  caseId,
  filters,
  isSubsetCreator = false,
}: {
  caseId: string | null;
  filters: MatterDocsFilters;
  isSubsetCreator?: boolean;
}) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useMutation<{ docIds: string[] }>([QueryEntity.DocIds, { caseId, filters, isSubsetCreator }], async () => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: { filters, isSubsetCreator: isSubsetCreator || undefined },
    });
    const res = await fetch(`${APIBaseChronos}/api/case/${caseId}/docs/filtered-ids`, fetchConfig);
    if (!res.ok) {
      throw new Error('Network response was not ok');
    }
    return res.json();
  });
};
